import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Save, Search, AlertCircle, History, Settings2, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import ProductEditor from "./ProductEditor";

/**
 * Shared Stock manager component.
 * - Used by Admin (sidebar tab) and Employee (with `stock` capability).
 * - Reads/writes /api/admin/stock — backend allows admin/editor or employee with stock cap.
 * - Admins & editors also get an inline ✏️ edit-product button (full ProductEditor)
 *   and a 🗑️ delete-product button (with cascade warning) per row.
 */
export default function StockManager({ heading = "Stock", eyebrow = "Inventory" }) {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "editor";

    const [rows, setRows] = useState([]);
    const [edits, setEdits] = useState({});       // {key: number}
    const [notes, setNotes] = useState({});       // {key: string}
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [editProductId, setEditProductId] = useState(null);
    const [deleteProduct, setDeleteProduct] = useState(null);   // {product_id, product_name, sizesCount}
    const [deletingBusy, setDeletingBusy] = useState(false);

    const keyOf = (r) => `${r.product_id}::${r.size_id}`;

    const load = async () => {
        try {
            const { data } = await api.get("/admin/stock");
            setRows(data);
            setEdits({});
            setNotes({});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load stock");
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter((r) =>
            r.product_name.toLowerCase().includes(needle)
            || (r.sku || "").toLowerCase().includes(needle)
            || (r.size_label || "").toLowerCase().includes(needle));
    }, [rows, q]);

    const dirtyKeys = Object.keys(edits).filter(
        (k) => rows.find((r) => keyOf(r) === k)?.stock !== Number(edits[k]),
    );

    const saveAll = async () => {
        if (dirtyKeys.length === 0) return;
        setBusy(true);
        const items = dirtyKeys.map((k) => {
            const r = rows.find((x) => keyOf(x) === k);
            return { product_id: r.product_id, size_id: r.size_id, stock: Number(edits[k]), note: notes[k] || null };
        });
        try {
            const { data } = await api.put("/admin/stock", { items });
            toast.success(`Updated ${data.updated} item${data.updated === 1 ? "" : "s"}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const askDeleteProduct = (r) => {
        const sizesCount = rows.filter((x) => x.product_id === r.product_id).length;
        setDeleteProduct({ product_id: r.product_id, product_name: r.product_name, sizesCount });
    };

    const confirmDeleteProduct = async () => {
        if (!deleteProduct) return;
        setDeletingBusy(true);
        try {
            await api.delete(`/products/${deleteProduct.product_id}`);
            toast.success(`Deleted '${deleteProduct.product_name}'`);
            setDeleteProduct(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't delete product");
        } finally {
            setDeletingBusy(false);
        }
    };

    return (
        <div data-testid="stock-manager">
            <Toaster position="top-center" />
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700">{eyebrow}</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">{heading}</h1>
                    <p className="text-sm text-stone-600 mt-2">Daily stock counts. Type a new value, optionally add a note, then "Save updates".{canManage && " Use the gear icon to edit the full product, or the trash icon to delete it entirely."}</p>
                </div>
                <div className="flex gap-3 items-end">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product or SKU…" className="rounded-none border-stone-300 pl-9 w-72" data-testid="stock-search" />
                    </div>
                    <Button onClick={saveAll} disabled={busy || dirtyKeys.length === 0} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" data-testid="stock-save">
                        <Save className="w-4 h-4 mr-2" />Save updates {dirtyKeys.length > 0 && `(${dirtyKeys.length})`}
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Product</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Size</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">SKU</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Current</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">New count</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Note (optional)</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Last updated</th>
                            {canManage && <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={canManage ? 8 : 7} className="p-10 text-center text-stone-500">No products match.</td></tr>
                        ) : filtered.map((r) => {
                            const k = keyOf(r);
                            const newVal = edits[k] ?? r.stock;
                            const dirty = Number(newVal) !== r.stock;
                            const low = r.stock < 5;
                            return (
                                <tr key={k} className={`border-b border-stone-100 ${dirty ? "bg-amber-50/40" : ""}`} data-testid={`stock-row-${k}`}>
                                    <td className="p-3 font-medium text-slate-900">{r.product_name}</td>
                                    <td className="p-3 text-stone-600">{r.size_label}</td>
                                    <td className="p-3 text-xs text-stone-500 font-mono">{r.sku || "—"}</td>
                                    <td className="p-3 text-right">
                                        <span className={`font-mono ${low ? "text-red-700" : "text-stone-700"}`}>{r.stock}</span>
                                        {low && <AlertCircle className="w-3.5 h-3.5 inline ml-1 text-red-700" aria-label="low stock" />}
                                    </td>
                                    <td className="p-3">
                                        <Input
                                            type="number"
                                            min={0}
                                            value={newVal}
                                            onChange={(e) => setEdits({ ...edits, [k]: e.target.value })}
                                            className="rounded-none border-stone-300 w-24"
                                            data-testid={`stock-input-${k}`}
                                        />
                                    </td>
                                    <td className="p-3">
                                        <Input
                                            value={notes[k] || ""}
                                            onChange={(e) => setNotes({ ...notes, [k]: e.target.value })}
                                            placeholder={dirty ? "Why the change?" : ""}
                                            className="rounded-none border-stone-300 w-56"
                                            data-testid={`stock-note-${k}`}
                                        />
                                    </td>
                                    <td className="p-3 text-xs text-stone-500 whitespace-nowrap">
                                        {r.last_updated ? (
                                            <span><History className="w-3.5 h-3.5 inline mr-1" />{new Date(r.last_updated).toLocaleString()} · {r.last_updated_by}</span>
                                        ) : "—"}
                                    </td>
                                    {canManage && (
                                        <td className="p-3 text-right">
                                            <div className="inline-flex gap-1">
                                                <button
                                                    onClick={() => setEditProductId(r.product_id)}
                                                    className="text-amber-700 hover:bg-amber-50 border border-amber-300 p-1 inline-flex items-center"
                                                    data-testid={`stock-edit-product-${r.product_id}`}
                                                    title="Edit product (name, sizes, prices…)"
                                                >
                                                    <Settings2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => askDeleteProduct(r)}
                                                    className="text-red-700 hover:bg-red-50 border border-red-300 p-1 inline-flex items-center"
                                                    data-testid={`stock-delete-product-${r.product_id}`}
                                                    title="Delete entire product"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Full product editor */}
            <ProductEditor
                open={!!editProductId}
                onOpenChange={(o) => !o && setEditProductId(null)}
                productId={editProductId}
                onSaved={() => load()}
            />

            {/* Delete-product confirmation */}
            <AlertDialog open={!!deleteProduct} onOpenChange={(o) => !o && setDeleteProduct(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete '{deleteProduct?.product_name}'?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the product and <strong>all {deleteProduct?.sizesCount} of its size variants</strong> from the catalog. Past invoices, orders and packing slips that referenced it are preserved.
                            <span className="block mt-2 text-amber-700 text-xs">
                                Tip — to keep history but hide it from the catalog, use the gear icon → uncheck <strong>Active</strong> instead.
                            </span>
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="stock-delete-cancel">Keep</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDeleteProduct} disabled={deletingBusy} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="stock-delete-confirm">
                            Delete product
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
